import React, { useMemo, useRef, useState } from "react";
import { DEFAULT_COL_WIDTH, DEFAULT_ROW_HEIGHT, HEADER_COL_WIDTH, HEADER_ROW_HEIGHT } from "../../constants";
import { computePane, PANE_Z } from "./paneClip";
import { startObjectDrag, RESIZE_HANDLE_STYLE, SELECTION_FRAME_STYLE } from "./objectDrag";

/**
 * ExcelImage renders ONE pane-copy of an embedded image, mirroring
 * ExcelChart's freeze-pane behaviour: the part of an image inside the
 * frozen rows/columns stays pinned while the rest scrolls.
 *
 * In Edit mode the image can be dragged to move it, resized from the
 * corner handle, and selected (for Ctrl+C / Ctrl+V) — same as charts.
 */
function ExcelImage({
  image, colWidths, rowHeights, zoomScale, hiddenCols, hiddenRows,
  pane = "body",
  freezeX = HEADER_COL_WIDTH, freezeY = HEADER_ROW_HEIGHT,
  scrollX = 0, scrollY = 0,
  movable = false,  // move / resize only while the reader is editing
  adjust = null,    // { dx, dy, w, h } user move/resize, owned by VisualOverlay
  objKey,           // stable id of this object within the sheet
  onAdjust,         // (objKey, geom) => void
  selected = false,
  onSelect,         // (objKey, geom) => void
}) {
  const rootRef = useRef(null);
  const [interacting, setInteracting] = useState(false);
  // Compute position and size synchronously to avoid a flash at (0,0)
  const { position, imageSize } = useMemo(() => {
    if (!image.range) return { position: { top: 0, left: 0 }, imageSize: { width: image.width, height: image.height } };

    const range = image.range;
    const headerRowHeight = HEADER_ROW_HEIGHT;
    const headerColWidth = HEADER_COL_WIDTH; // 44px - matches CSS

    // Hidden rows/cols are not rendered by the grid — skip them here too.
    const isColHidden = (i) => !!(hiddenCols && hiddenCols.has && hiddenCols.has(i));
    const isRowHidden = (i) => !!(hiddenRows && hiddenRows.has && hiddenRows.has(i));

    // Calculate top-left position
    let tlLeft = 0, tlTop = 0;
    for (let i = 0; i < (range.tl?.col || 0); i++) {
      if (!isColHidden(i)) tlLeft += (colWidths[i] || DEFAULT_COL_WIDTH);
    }
    for (let i = 0; i < (range.tl?.row || 0); i++) {
      if (!isRowHidden(i)) tlTop += (rowHeights[i] || DEFAULT_ROW_HEIGHT);
    }

    // Add header offsets
    tlLeft += headerColWidth;
    tlTop += headerRowHeight;

    // Add EMU sub-cell offsets
    if (range.tl?.colOff) tlLeft += range.tl.colOff / 914400 * 96;
    if (range.tl?.rowOff) tlTop += range.tl.rowOff / 914400 * 96;

    let width = image.width;
    let height = image.height;

    // Calculate dimensions from bottom-right anchor
    if (range.br) {
      let brLeft = 0, brTop = 0;
      for (let i = 0; i < (range.br.col || 0); i++) {
        if (!isColHidden(i)) brLeft += (colWidths[i] || DEFAULT_COL_WIDTH);
      }
      for (let i = 0; i < (range.br.row || 0); i++) {
        if (!isRowHidden(i)) brTop += (rowHeights[i] || DEFAULT_ROW_HEIGHT);
      }
      brLeft += headerColWidth;
      brTop += headerRowHeight;

      if (range.br.colOff) brLeft += range.br.colOff / 914400 * 96;
      if (range.br.rowOff) brTop += range.br.rowOff / 914400 * 96;

      width = Math.max(20, brLeft - tlLeft);
      height = Math.max(20, brTop - tlTop);
    }

    return { position: { top: tlTop, left: tlLeft }, imageSize: { width, height } };
  }, [image, colWidths, rowHeights, hiddenCols, hiddenRows]);

  // Anchored geometry + the reader's move/resize (content px).
  const posLeft = position.left + (adjust && adjust.dx ? adjust.dx : 0);
  const posTop = position.top + (adjust && adjust.dy ? adjust.dy : 0);
  const imgW = adjust && adjust.w ? adjust.w : imageSize.width;
  const imgH = adjust && adjust.w ? adjust.h : imageSize.height;

  // Freeze-pane geometry for THIS pane (content px); null => nothing here.
  const paneGeom = useMemo(
    () => computePane(pane, posLeft, posTop, imgW, imgH, freezeX, freezeY, scrollX, scrollY),
    [pane, posLeft, posTop, imgW, imgH, freezeX, freezeY, scrollX, scrollY]
  );

  if (!paneGeom) return null;

  const beginDrag = (e, mode) => {
    if (!movable || e.button !== 0) return;
    if (typeof onSelect === "function") onSelect(objKey, { left: posLeft, top: posTop, width: imgW, height: imgH });
    setInteracting(true);
    startObjectDrag(
      e, mode, rootRef.current,
      { dx: adjust && adjust.dx ? adjust.dx : 0, dy: adjust && adjust.dy ? adjust.dy : 0, w: imgW, h: imgH },
      (g) => { if (typeof onAdjust === "function") onAdjust(objKey, g); },
      () => setInteracting(false),
      { minW: 20, minH: 20 },
    );
  };

  // Scale position (incl. pane pin offset) and size by zoomScale.
  const scaledTop = (posTop + (paneGeom.offsetY || 0)) * zoomScale;
  const scaledLeft = (posLeft + (paneGeom.offsetX || 0)) * zoomScale;
  const scaledWidth = imgW * zoomScale;
  const scaledHeight = imgH * zoomScale;
  const zIndex = PANE_Z[pane] || 10;
  const clipStyle = paneGeom.clipPath
    ? { clipPath: paneGeom.clipPath, WebkitClipPath: paneGeom.clipPath }
    : null;

  return (
    <div
      ref={rootRef}
      className="excel-image-overlay"
      data-pane={pane}
      data-cl={posLeft}
      data-ct={posTop}
      data-cw={imgW}
      data-ch={imgH}
      title={movable ? "Drag to move · drag the corner handle to resize" : undefined}
      onMouseDown={movable ? (e) => beginDrag(e, "move") : undefined}
      style={{
        position: 'absolute',
        top: scaledTop,
        left: scaledLeft,
        width: scaledWidth,
        height: scaledHeight,
        pointerEvents: 'auto',
        zIndex: interacting ? 40 : zIndex,
        cursor: movable ? 'move' : undefined,
        userSelect: 'none',
        boxSizing: 'border-box',
        ...(interacting ? {} : clipStyle),
      }}
    >
      <img
        src={image.dataUrl}
        alt=""
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          maxWidth: 'none',
          maxHeight: 'none',
        }}
        draggable={false}
      />
      {/* Selection frame drawn INSIDE the box (an outline would be cut off
          by the freeze-pane clip-path), and the resize handle only on the
          SELECTED image — not on every image as soon as Edit is on. */}
      {movable && (selected || interacting) && (
        <div style={SELECTION_FRAME_STYLE} />
      )}
      {movable && selected && (
        <div onMouseDown={(e) => beginDrag(e, "resize")} style={RESIZE_HANDLE_STYLE} />
      )}
    </div>
  );
}

// PERF: memo'd — image objects and size arrays are stable per sheet.
export default React.memo(ExcelImage);
